import { useCallback, useEffect, useState } from 'react';
import { addDaysToLocalDate, defaultMeasureUnit, localDateAt, localDateTimeToInstant, parseSkipNote, postponeChoices, renderMedicationLabel, SKIP_REASONS, type SkipReason } from '@drbell/core';
import {
  api, ApiError, type AdherenceSummary, type PatientSummary, type TimelineDose, type UnconfirmedDose,
} from '../api.ts';
import { ReadingForm } from './Measurements.tsx';


/**
 * Today, for one person.
 *
 * The question this screen answers is the only one a caregiver actually has:
 * *has she taken her tablets?* Everything is arranged around making that
 * readable in about two seconds — anything unresolved first, then the day in
 * order with a line marking now.
 *
 * Deliberately not a dashboard of charts. A worried daughter at 9pm does not
 * want a 78% adherence figure; she wants to know whether the evening tablet
 * has been taken and, if not, whether to ring.
 */
/** How early before its time a dose can be marked taken here. */
const MARK_EARLY_MS = 60 * 60_000;

export function Timeline({ patient, userId, canAcknowledge = true }: { patient: PatientSummary; userId: string; canAcknowledge?: boolean }) {
  const [doses, setDoses] = useState<TimelineDose[] | null>(null);
  const [adherence, setAdherence] = useState<AdherenceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [unconfirmed, setUnconfirmed] = useState<UnconfirmedDose[]>([]);

  const load = useCallback(() => {
    const now = Date.now();
    const from = startOfDay(now, patient.timezone);
    const to = startOfNextDay(now, patient.timezone);
    api
      .get<{ doses: TimelineDose[] }>(
        `/api/patients/${patient.id}/doses?from=${from}&to=${to}`,
      )
      .then((r) => setDoses(r.doses))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));

    api
      .get<AdherenceSummary>(`/api/patients/${patient.id}/adherence?days=30`)
      .then(setAdherence)
      .catch(() => setAdherence(null));

    // Only a guardian can confirm, and only in kid mode; anyone else gets an
    // empty list or a refusal, and the section simply does not appear.
    api
      .get<{ kidMode: boolean; doses: UnconfirmedDose[] }>(`/api/patients/${patient.id}/doses/unconfirmed`)
      .then((r) => setUnconfirmed(r.kidMode ? r.doses : []))
      .catch(() => setUnconfirmed([]));
  }, [patient.id, patient.timezone]);

  useEffect(load, [load]);

  /*
   * Keep "today" current — B2C audit. It loaded once: a tab left open all
   * day still showed the morning's doses as due after the phone had marked
   * them taken, and never moved past midnight. Every two minutes while the
   * tab is visible, and at once when it becomes visible again.
   */
  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') load(); };
    const timer = window.setInterval(tick, 120_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  // The "now" line has to move, or a tab left open all evening lies about
  // which doses are still ahead.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const markTaken = async (doseId: string, skip?: { skipReason?: SkipReason }) => {
    setBusy(doseId);
    setError(null);
    try {
      // A skip is recorded the same way, with its reason (docs/33 B1).
      await api.post(`/api/doses/${doseId}/record`, skip ? { status: 'skipped', ...skip } : { status: 'taken' });
      load();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 403
          ? 'You can see this record but not record doses on it.'
          : e instanceof ApiError ? e.message : String(e),
      );
    } finally {
      setBusy(null);
    }
  };

  /*
   * "I'll handle it" — RX-CARE-12, on the web too.
   *
   * It was a lock-screen button on the phone and nowhere else, so a daughter
   * at her desk who rang her mother about the late tablet had no way to tell
   * her brother she had it in hand, and he was nudged again. It does not
   * record the dose, and it does not stop the missed alert.
   */
  const claim = async (doseId: string) => {
    setBusy(doseId);
    setError(null);
    try {
      const r = await api.post<{ alreadyClaimed?: boolean; claimedBy?: string | null }>(`/api/doses/${doseId}/claim`);
      if (r.alreadyClaimed) setError(`${r.claimedBy ?? 'Someone else'} is already handling this one.`);
      load();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 403
          ? 'You can see this record but not act on its doses.'
          : e instanceof ApiError ? e.message : String(e),
      );
    } finally {
      setBusy(null);
    }
  };

  /*
   * Move this time — docs/33 C9, parity with the phone. One dose still to
   * come, to a later time today; its usual time again puts it back. The rule
   * is core's, the same one the server enforces.
   */
  const choicesFor = (d: TimelineDose) => {
    if (!canAcknowledge || !doses || d.dose.status !== 'pending' || d.dose.dueAt <= now) return [];
    const firstDueAt = d.dose.postponedFrom ?? d.dose.dueAt;
    const same = (x: TimelineDose) => (d.dose.medicationId ? x.dose.medicationId === d.dose.medicationId : x.medication.name === d.medication.name);
    const next = doses.filter((x) => x.dose.id !== d.dose.id && same(x) && x.dose.dueAt > firstDueAt).map((x) => x.dose.dueAt);
    return postponeChoices({
      dueAt: d.dose.dueAt, firstDueAt, now, dayEnd: startOfNextDay(now, patient.timezone),
      nextDueAt: next.length ? Math.min(...next) : null, isCritical: d.medication.isCritical,
    });
  };
  const move = async (doseId: string, until: number) => {
    setBusy(doseId);
    setError(null);
    try {
      await api.post(`/api/doses/${doseId}/postpone`, { until });
      load();
    } catch (e) {
      setError(e instanceof ApiError ? `Could not move it: ${e.message}` : String(e));
    } finally {
      setBusy(null);
    }
  };

  /** A guardian countersigning a child's own "taken" — RX-A11Y-03. */
  const confirmDose = async (doseId: string) => {
    setBusy(doseId);
    setError(null);
    try {
      await api.post(`/api/doses/${doseId}/confirm`);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (error && !doses) {
    return <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</p>;
  }
  if (!doses) return <p className="text-(--muted)">Loading…</p>;

  const needsAttention = doses.filter(
    (d) => d.dose.status === 'missed'
      || (d.dose.status === 'pending' && d.dose.windowEnd < now),
  );
  const rest = doses.filter((d) => !needsAttention.includes(d));

  return (
    <section className="space-y-6">
      <Headline doses={doses} now={now} timezone={patient.timezone} />

      <PhoneHealth patient={patient} />

      {error && (
        <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      )}

      {unconfirmed.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-medium text-(--accent)">
            {patient.displayName} says these were taken — confirm?
          </h2>
          <ul className="divide-y divide-(--line) rounded-xl border border-(--accent)">
            {unconfirmed.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="w-16 shrink-0 tabular-nums text-sm text-(--muted)">{timeOf(u.dueAt, patient.timezone)}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">
                    {renderMedicationLabel({ name: u.medicationName, nameAlt: u.medicationNameAlt }).text}
                  </div>
                  <div className="text-xs text-(--muted)">
                    {u.doseQuantity} {u.doseUnit}
                    {u.takenAt ? ` · said taken at ${timeOf(u.takenAt, patient.timezone)}` : ''}
                  </div>
                </div>
                <button
                  disabled={busy === u.id}
                  onClick={() => void confirmDose(u.id)}
                  className="min-h-11 rounded-lg bg-(--accent) px-3 text-sm font-medium text-white disabled:opacity-40"
                >
                  {busy === u.id ? 'Saving…' : 'Confirm'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {needsAttention.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-medium text-red-500">Needs attention</h2>
          <ul className="divide-y divide-(--line) rounded-xl border border-red-500/40 bg-red-500/5">
            {needsAttention.map((d) => (
              <DoseRow
                key={d.dose.id}
                entry={d}
                now={now}
                timezone={patient.timezone}
                busy={busy === d.dose.id}
                onTaken={() => void markTaken(d.dose.id)}
                onSkip={(reason) => void markTaken(d.dose.id, reason ? { skipReason: reason } : {})}
                onClaim={() => void claim(d.dose.id)}
                moveChoices={choicesFor(d)}
                onMove={(at) => void move(d.dose.id, at)}
                userId={userId}
                canAcknowledge={canAcknowledge}
                patientId={patient.id}
                onRead={load}
              />
            ))}
          </ul>
        </div>
      )}

      <div>
        <h2 className="mb-2 text-sm font-medium text-(--muted)">Today</h2>
        {rest.length === 0 ? (
          <p className="rounded-xl border border-dashed border-(--line) px-4 py-8 text-center text-(--muted)">
            Nothing else scheduled today.
          </p>
        ) : (
          <ul className="divide-y divide-(--line) rounded-xl border border-(--line)">
            {rest.map((d, i) => (
              <li key={d.dose.id}>
                {crossesNow(rest, i, now) && <NowLine timezone={patient.timezone} now={now} />}
                <DoseRow
                  entry={d}
                  now={now}
                  timezone={patient.timezone}
                  busy={busy === d.dose.id}
                  onTaken={() => void markTaken(d.dose.id)}
                  onSkip={(reason) => void markTaken(d.dose.id, reason ? { skipReason: reason } : {})}
                  onClaim={() => void claim(d.dose.id)}
                  moveChoices={choicesFor(d)}
                  onMove={(at) => void move(d.dose.id, at)}
                  userId={userId}
                  canAcknowledge={canAcknowledge}
                  patientId={patient.id}
                  onRead={load}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {adherence && adherence.byMedication.length > 0 && <Last30Days adherence={adherence} />}
    </section>
  );
}

/**
 * One sentence at the top, because that is what gets read.
 *
 * Counts unresolved doses rather than reporting a percentage: "one tablet not
 * taken" is actionable and "94%" is not.
 */
function Headline({ doses, now, timezone }: { doses: TimelineDose[]; now: number; timezone: string }) {
  const overdue = doses.filter(
    (d) => d.dose.status === 'missed' || (d.dose.status === 'pending' && d.dose.windowEnd < now),
  ).length;
  const next = doses.find((d) => d.dose.status === 'pending' && d.dose.dueAt >= now);
  const takenToday = doses.filter((d) => d.dose.status === 'taken').length;

  const tone = overdue > 0 ? 'border-red-500/40 bg-red-500/5' : 'border-(--line) bg-(--panel)';

  return (
    <div className={`rounded-xl border p-5 ${tone}`}>
      <p className="text-xl font-semibold">
        {overdue > 0
          ? `${overdue} dose${overdue === 1 ? '' : 's'} not taken`
          : next
            ? `Next at ${timeOf(next.dose.dueAt, timezone)}`
            : takenToday > 0
              ? 'All done for today'
              : 'Nothing scheduled today'}
      </p>
      <p className="mt-1 text-sm text-(--muted)">
        {takenToday} of {doses.length} taken today
        {next && overdue > 0 ? ` · next at ${timeOf(next.dose.dueAt, timezone)}` : ''}
      </p>
    </div>
  );
}

/**
 * How a medicine is taken, in the words a caregiver would use.
 *
 * `any` maps to nothing rather than to the word "any": most prescriptions do
 * not care, and printing a noise word beside every one of them would teach
 * people to stop reading the line that sometimes says "on an empty stomach".
 */
const FOOD_LABEL: Record<string, string | null> = {
  before_food: 'Before food',
  with_food: 'With food',
  after_food: 'After food',
  empty_stomach: 'On an empty stomach',
  any: null,
};

function DoseRow({ entry, now, timezone, busy, onTaken, onClaim, onSkip, moveChoices, onMove, userId, canAcknowledge, patientId, onRead }: {
  entry: TimelineDose;
  patientId: string;
  /** A reading was typed for this measurement: reload the day. */
  onRead: () => void;
  /** The later times this dose may move to today (docs/33 C9); empty when it may not. */
  moveChoices: Array<{ at: number; restore: boolean }>;
  onMove: (at: number) => void;
  now: number;
  canAcknowledge: boolean;
  timezone: string;
  busy: boolean;
  onTaken: () => void;
  onClaim: () => void;
  /** Recorded as skipped, with a reason if one was chosen (docs/33 B1). */
  onSkip: (reason: SkipReason | null) => void;
  userId: string;
}) {
  const { dose, medication } = entry;
  const [skipOpen, setSkipOpen] = useState(false);
  /** A measurement's reminder is answered by typing the number (docs/33 D1). */
  const measuring = medication.kind === 'measurement' && medication.measure ? medication.measure : null;
  const [reading, setReading] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const overdue = dose.status === 'pending' && dose.windowEnd < now;
  const foodLabel = FOOD_LABEL[entry.foodRelation ?? 'any'];

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="w-16 shrink-0 tabular-nums text-sm text-(--muted)">
        {timeOf(dose.dueAt, timezone)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="font-medium">
          {/* The packet name first, always. A second label the user added
              follows in brackets; it never replaces the one on the strip. */}
          {renderMedicationLabel({ name: medication.name, nameAlt: medication.nameAlt }).text}
          {medication.strength && (
            <span className="text-(--muted)"> {medication.strength}{medication.strengthUnit}</span>
          )}
          {medication.isCritical && (
            <span className="ml-2 rounded-full border border-red-500/40 px-2 py-0.5 text-xs text-red-500">
              critical
            </span>
          )}
        </div>
        <div className="text-xs text-(--muted)">
          {/*
            How it is taken, next to what it is — RX-MED-02.
            
            Defect 60 was this same field missing from the patient's own card,
            where the code had hardcoded "after food" for everything. Fixing
            it there and not here would have left a daughter checking on her
            mother with no way to know the thyroid tablet has to be taken on
            an empty stomach — which is the single instruction that makes it
            work. `any` prints nothing.
          */}
          {foodLabel && <span className="text-(--brand)">{foodLabel} · </span>}
          <StatusLine dose={dose} overdue={overdue} timezone={timezone} measuring={measuring !== null} />
          {dose.postponedFrom && dose.status === 'pending' && <> · moved from {timeOf(dose.postponedFrom, timezone)}</>}
        </div>
      </div>

      {moveChoices.length > 0 && !moveOpen && (
        <button
          disabled={busy}
          onClick={() => setMoveOpen(true)}
          className="rounded-lg border border-(--line) min-h-11 px-3 text-sm hover:border-(--accent) disabled:opacity-40"
        >
          Move this time…
        </button>
      )}

      {/* Only for someone who may act on it, and "Mark taken" only once the
          dose is close: a viewer got two buttons that always failed, and a
          stray click could record tomorrow's tablet as taken. B2C audit. */}
      {canAcknowledge && dose.status === 'pending' && dose.dueAt < now && (
        dose.claimedAt ? (
          <span className="text-xs text-(--muted)">
            {dose.claimedByUserId === userId ? 'You are handling this' : 'Someone is handling this'}
          </span>
        ) : (
          <button
            disabled={busy}
            onClick={onClaim}
            className="rounded-lg border border-(--line) min-h-11 px-3 text-sm hover:border-(--accent) disabled:opacity-40"
          >
            I&rsquo;ll handle it
          </button>
        )
      )}

      {canAcknowledge && dose.status === 'pending' && dose.dueAt - MARK_EARLY_MS <= now && !skipOpen && (
        <button
          disabled={busy}
          onClick={() => setSkipOpen(true)}
          className="rounded-lg border border-(--line) min-h-11 px-3 text-sm hover:border-(--accent) disabled:opacity-40"
        >
          Skipped…
        </button>
      )}

      {canAcknowledge && dose.status === 'pending' && dose.dueAt - MARK_EARLY_MS <= now && measuring && !reading && (
        <button
          disabled={busy}
          onClick={() => setReading(true)}
          className="rounded-lg bg-(--accent) min-h-11 px-3 text-sm font-medium text-white disabled:opacity-40"
        >
          Type the reading
        </button>
      )}

      {reading && measuring && (
        <ReadingForm
          patientId={patientId}
          item={{ medicationId: dose.medicationId ?? '', name: medication.name, measure: measuring, unit: medication.measureUnit ?? defaultMeasureUnit(measuring) }}
          doseEventId={dose.id}
          onDone={() => { setReading(false); onRead(); }}
          onCancel={() => setReading(false)}
        />
      )}

      {canAcknowledge && dose.status === 'pending' && dose.dueAt - MARK_EARLY_MS <= now && !measuring && (
        <button
          disabled={busy}
          onClick={onTaken}
          className="rounded-lg bg-(--accent) min-h-11 px-3 text-sm font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Saving…' : 'Mark taken'}
        </button>
      )}

      {moveOpen && (
        <div className="basis-full rounded-lg border border-(--line) p-3 text-sm">
          <div className="mb-2 font-medium">Ring at what time instead? Only this once.</div>
          <div className="flex flex-wrap gap-2">
            {moveChoices.map(({ at, restore }) => (
              <button
                key={at}
                disabled={busy}
                onClick={() => { setMoveOpen(false); onMove(at); }}
                className="rounded-full border border-(--line) min-h-11 px-3 hover:border-(--accent) disabled:opacity-40"
              >
                {restore ? `Back to ${timeOf(at, timezone)}` : timeOf(at, timezone)}
              </button>
            ))}
            <button onClick={() => setMoveOpen(false)} className="min-h-11 px-3 text-(--muted) underline">Cancel</button>
          </div>
        </div>
      )}

      {skipOpen && (
        <div className="basis-full rounded-lg border border-(--line) p-3 text-sm">
          <div className="mb-2 font-medium">Record this dose as skipped? Why, if you know:</div>
          <div className="flex flex-wrap gap-2">
            {SKIP_REASONS.map((r) => (
              <button
                key={r}
                disabled={busy}
                onClick={() => { setSkipOpen(false); onSkip(r); }}
                className="rounded-full border border-(--line) min-h-11 px-3 hover:border-(--accent) disabled:opacity-40"
              >
                {SKIP_LABEL[r]}
              </button>
            ))}
            <button
              disabled={busy}
              onClick={() => { setSkipOpen(false); onSkip(null); }}
              className="rounded-full border border-(--line) min-h-11 px-3 hover:border-(--accent) disabled:opacity-40"
            >
              No reason
            </button>
            <button onClick={() => setSkipOpen(false)} className="min-h-11 px-3 text-(--muted) underline">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Says what happened in words, not a status chip.
 *
 * "Taken at 8:34 PM" and "snoozed 3 times" are things a caregiver can act on;
 * a coloured dot labelled `pending` is not.
 */
function StatusLine({ dose, overdue, timezone, measuring }: {
  dose: TimelineDose['dose']; overdue: boolean; timezone: string; measuring: boolean;
}) {
  if (dose.status === 'taken') {
    return (
      <>
        Taken{dose.takenAt ? ` at ${timeOf(dose.takenAt, timezone)}` : ''}
        {dose.source === 'caregiver' && ' · recorded by a caregiver'}
        {dose.source === 'staff' && ' · recorded by staff'}
      </>
    );
  }
  if (dose.status === 'missed') return <span className="text-red-500">Missed</span>;
  if (dose.status === 'skipped') {
    const r = parseSkipNote(dose.note);
    const why = [r.reason ? SKIP_LABEL[r.reason] : null, r.words].filter(Boolean).join(' — ');
    return <>Skipped{why ? `: ${why}` : ''}</>;
  }
  if (dose.status === 'refused') return <>Refused</>;
  if (overdue) return <span className="text-red-500">Not taken — window closed</span>;
  return (
    <>
      {measuring ? 'To measure' : `${dose.doseQuantity} ${dose.doseUnit}`}
      {dose.snoozeCount > 0 && (
        <span className="text-amber-500"> · put off {dose.snoozeCount} time{dose.snoozeCount === 1 ? '' : 's'}</span>
      )}
    </>
  );
}

function NowLine({ timezone, now }: { timezone: string; now: number }) {
  return (
    <div className="flex items-center gap-2 px-4 py-1">
      <div className="h-px flex-1 bg-(--accent)" />
      <span className="text-xs font-medium text-(--accent)">{timeOf(now, timezone)}</span>
      <div className="h-px w-4 bg-(--accent)" />
    </div>
  );
}

function Last30Days({ adherence }: { adherence: AdherenceSummary }) {
  return (
    <div className="rounded-xl border border-(--line) p-5">
      <h2 className="mb-3 text-sm font-medium text-(--muted)">
        Last {adherence.days} days
      </h2>
      <ul className="space-y-2 text-sm">
        {adherence.byMedication.map((m) => (
          <li key={m.medicationId} className="flex flex-wrap items-baseline gap-2">
            <span className="font-medium">{m.medicationName}</span>
            <span className="text-(--muted)">
              {/* Counts, never a percentage. "28 taken, 2 missed" is something
                  to act on; "93%" invites reading it as a grade. */}
              {m.counts.taken ?? 0} taken
              {(m.counts.missed ?? 0) > 0 && `, ${m.counts.missed} missed`}
              {(m.counts.skipped ?? 0) > 0 && `, ${m.counts.skipped} skipped`}
              {(m.counts.pending ?? 0) > 0 && `, ${m.counts.pending} still to come`}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-(--muted)">
        This shows what was recorded in the app, not what was swallowed.
      </p>
    </div>
  );
}

// ── time helpers ────────────────────────────────────────────────────────────

/** `8:30 PM` in the patient's own timezone, not the caregiver's. */
/** Why a dose was skipped, as the phone's Skip sheet says it (docs/33 B1). */
const SKIP_LABEL: Record<SkipReason, string> = {
  unwell: 'felt unwell',
  out_of_stock: 'none left',
  doctor_said: 'the doctor said so',
  other: 'another reason',
};

function timeOf(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-IN', {
    hour: 'numeric', minute: '2-digit', timeZone,
  }).format(ms);
}

/**
 * Midnight today where the PATIENT is.
 *
 * A daughter in London checking on her mother in Chennai must see her
 * mother's day, not her own — otherwise the evening tablet appears under
 * tomorrow, or vanishes.
 */
function startOfDay(ms: number, timeZone: string): number {
  /*
   * Core's zone arithmetic — B2C audit. This measured the offset by reading
   * the clock at UTC midnight, which is the previous evening anywhere west of
   * Greenwich: a family in New York was shown a "today" that began at 7 pm
   * the day before. Core already does this right, for the scheduler.
   */
  return localDateTimeToInstant(localDateAt(ms, timeZone), '00:00', timeZone);
}

/** The next local midnight — 23 or 25 hours away on a clock-change day. */
function startOfNextDay(ms: number, timeZone: string): number {
  return localDateTimeToInstant(addDaysToLocalDate(localDateAt(ms, timeZone), 1), '00:00', timeZone);
}

function crossesNow(list: TimelineDose[], i: number, now: number): boolean {
  const prev = list[i - 1];
  return list[i]!.dose.dueAt >= now && (!prev || prev.dose.dueAt < now);
}

interface Phone {
  id: string;
  label: string;
  canRing: boolean | null;
  lastSeenAt: number | null;
  quiet: boolean;
  /** Whether it is in sync (@drbell/core PhoneSync). Absent from an API older than 0039. */
  sync?: {
    remindersFetchedAt: number | null;
    nudgedAt: number | null;
    nudge: 'answered' | 'waiting' | 'no_answer' | null;
  };
}

/** A time today as "08:02"; another day as "3 Oct, 08:02". */
function clock(ms: number): string {
  const d = new Date(ms);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString()
    ? time
    : `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${time}`;
}

const ANSWER = { answered: 'answered', waiting: 'waiting for an answer', no_answer: 'no answer' } as const;

/**
 * Can their phone ring their reminders? — RX-REM-15.
 *
 * Whether each phone this person's reminders ring on can ring them, and when
 * it last checked in. Not which setting is off: that is theirs to see, and the
 * phone walks them through fixing it. The caregiver's part is to know, and to
 * ask them to open DrBell.
 */
function PhoneHealth({ patient }: { patient: PatientSummary }) {
  const [phones, setPhones] = useState<Phone[] | null>(null);
  useEffect(() => {
    api
      .get<{ phones: Phone[] }>(`/api/patients/${patient.id}/phones`)
      .then((r) => setPhones(r.phones))
      .catch(() => setPhones(null));
  }, [patient.id]);

  if (!phones || phones.length === 0) return null;
  const trouble = phones.filter((p) => p.canRing === false || p.quiet);

  return (
    <div
      className={`rounded-xl border px-4 py-3 text-sm ${
        trouble.length > 0 ? 'border-red-500/40 bg-red-500/5' : 'border-(--line)'
      }`}
    >
      <h2 className="mb-1 font-medium">{patient.displayName}’s phone</h2>
      <ul className="space-y-1">
        {phones.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center gap-2">
            <span>{p.label}</span>
            <span
              className={`rounded px-2 py-0.5 text-xs ${
                p.canRing === false || p.quiet ? 'bg-red-500/10 text-red-600' : 'bg-(--accent-soft) text-(--accent)'
              }`}
            >
              {p.quiet
                ? 'has not checked in for a day'
                : p.canRing === false
                  ? 'cannot ring reminders'
                  : p.canRing === true
                    ? 'can ring reminders'
                    : 'not checked yet'}
            </span>
            <span className="text-xs text-(--muted)">
              last checked in {p.lastSeenAt ? new Date(p.lastSeenAt).toLocaleString() : 'never'}
            </span>
            {/*
              In sync, not just able to ring: a phone that last fetched its
              reminders on Tuesday rings Tuesday's medicines. Each part only
              when there is something to say -- an older app reports no fetch.
            */}
            {p.sync?.remindersFetchedAt ? (
              <span className="text-xs text-(--muted)">· reminders up to date at {clock(p.sync.remindersFetchedAt)}</span>
            ) : null}
            {p.sync?.nudgedAt && p.sync.nudge ? (
              <span className={`text-xs ${p.sync.nudge === 'no_answer' ? 'text-amber-600' : 'text-(--muted)'}`}>
                · DrBell checked on it at {clock(p.sync.nudgedAt)} — {ANSWER[p.sync.nudge]}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {trouble.length > 0 && (
        <p className="mt-2 text-sm">
          Reminders may not ring on this phone. Ask {patient.displayName} to switch it on and open
          DrBell: it shows them, step by step, what to turn back on. Until then, please check they take
          their medicines.
        </p>
      )}
    </div>
  );
}

